import { inject, Injectable } from '@angular/core';
import { Listing } from '@df/catalog-ui/listing/listing';
import { AbstractAsyncEngine } from '@df/catalog-ui/listing/sort/abstract-async.engine';
import { IListingSortEngine } from '@df/catalog-ui/listing/sort/listing-sort';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { Product } from '@df/catalog/product/product';
import { take, takeUntil, timer } from 'rxjs';
import { KlevuSearchService } from './klevu-search.service';
import { KlevuTrackService } from './klevu-track.service';
import { App } from '@df/core/app';

declare module '@df/catalog/category/category' {
    interface ICategoryDataV3 {
        use_magento_merchandising?: boolean;
    }
}

@Injectable({
    providedIn: 'root'
})
export class ListingSortKlevuEngine extends AbstractAsyncEngine implements IListingSortEngine<Product> {
    private klevuTrackService = inject(KlevuTrackService);
    private klevuSearchService = inject(KlevuSearchService);
    private app = inject(App);

    protected override fetch(
        listing: Listing<Product>,
        collection: Product[],
        cb: (err: Error | string | null, ids: number[]) => void
    ): void {
        const sourceModel: Category<ICategoryDataV3> | undefined = listing.config.source;

        // Do not search if Magento merchandising is enabled
        if (sourceModel?.data?.use_magento_merchandising) {
            return;
        }

        // Search only if ab data are loaded
        if (this.app.isPlatformBrowser) {
            this.klevuTrackService.abDataLoaded.pipe(take(2), takeUntil(timer(2000))).subscribe(abDataLoaded => {
                if (abDataLoaded) {
                    this.klevuCategoryOrder(listing, cb);
                }
            });
        } else {
            this.klevuCategoryOrder(listing, cb);
        }
    }

    protected klevuCategoryOrder(listing: Listing<Product>, cb: (err: Error | string | null, ids: number[]) => void): void {
        const sourceModel: Category<ICategoryDataV3> | undefined = listing.config.source;
        if (sourceModel?.type === 'category' && (sourceModel!.verbosity || 0) >= 3) {
            this.klevuSearchService
                .sendCategoryOrder(<Category<ICategoryDataV3>>sourceModel)
                .then(ids => {
                    this.runCallback(listing, cb, ids);
                    this.klevuTrackService.trackCategoryOrder(<Category<ICategoryDataV3>>sourceModel);
                })
                .catch(err => {
                    cb(err, []);
                });
        } else {
            cb('Invalid source', []);
        }
    }

    protected runCallback(listing: Listing<Product>, cb: (err: Error | string | null, ids: number[]) => void, ids?: number[]) {
        cb(null, ids || []);
    }
}
